import { createServerFn } from '@tanstack/react-start';
import { getRequestHeaders } from '@tanstack/react-start/server';
import { parse } from 'cookie-es';

import { envConfig } from '#constants/envConfig';
import { isStaleServerFnError } from '#utils/isStaleServerFnError';
import { reloadOnce } from '#utils/reloadOnce';
import { isLikelyJwtToken } from './isLikelyJwtToken';

export const checkIsAuth = createServerFn({ method: 'GET' }).handler(async () => {
  const headers = getRequestHeaders();
  const cookie = parse(headers.get('cookie') ?? '');

  return {
    isAuth: isLikelyJwtToken(cookie.authToken),
  };
});

export type AuthStatus = 'ok' | 'error' | 'pending';

export type AuthState = {
  isAuth: boolean;
  status: AuthStatus;
};

const CHECK_IS_AUTH_TIMEOUT_MS = 5_000;
const STALE_BUNDLE_RELOAD_KEY = 'auth-stale-bundle:reloaded';

/**
 * Последнее подтверждённое сервером состояние авторизации — фолбэк на случай сбоя вызова.
 *
 * <p>Пишется только в браузере: на сервере модуль общий для всех запросов, и кеш здесь протёк бы
 * между пользователями. На SSR фолбэк остаётся false — там куку читают заново каждым запросом.
 */
let lastKnownIsAuth = false;

const withTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`checkIsAuth timed out after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * Returns the current auth state, with a hard timeout.
 *
 * Why no singleton: previously this function memoized a single in-flight Promise
 * across all callers. If the underlying server-fn fetch ever wedged (Cloudflare
 * pool, SSR worker stuck, HMR-orphaned promise), that one stuck Promise poisoned
 * every subsequent call, freezing the dashboard loader forever and rendering
 * Page Unresponsive. Each call now stands alone, and the timeout guarantees the
 * loader unblocks within N seconds — degraded login (status='error' → guard
 * routes to /sign-in) is strictly better than a frozen tab.
 *
 * The timeout MUST cover the SSR (server) path too — not only client navigation.
 * During the initial document render beforeLoad runs on the server; a wedged
 * server-fn there stalls the streamed HTML before the bootstrap <script> tags are
 * flushed, so the tab shows a blank page with the document stuck "pending" and
 * zero JS/CSS requested (no chunks, no hydration). One timed path keeps both SSR
 * and client navigation unblockable.
 */
export const safeCheckIsAuth = async (): Promise<AuthState> => {
  // Native shell: there is no SSR server to run the `checkIsAuth` server-fn
  // against, and no auth cookie — the source of truth is the stored JWT. Read it
  // directly so the `_authed` guard behaves correctly (redirect when logged out).
  if (envConfig.IS_NATIVE) {
    const { getNativeToken } = await import('#features/api/utils/nativeAuth');
    const token = await getNativeToken();
    return { isAuth: isLikelyJwtToken(token ?? undefined), status: 'ok' };
  }

  try {
    const { isAuth } = await withTimeout(checkIsAuth(), CHECK_IS_AUTH_TIMEOUT_MS);
    if (!envConfig.IN_SERVER) {
      lastKnownIsAuth = isAuth;
    }
    return { isAuth, status: 'ok' };
  } catch (error) {
    if (envConfig.IN_DEV) {
      console.warn('checkIsAuth failed, defaulting to last known auth state.', error);
    }

    // Старый клиентский бандл зовёт серверную функцию по идентификатору, которого в новой сборке
    // уже нет. Свежая загрузка страницы это чинит — ровно как с протухшими чанками.
    if (!envConfig.IN_SERVER && isStaleServerFnError(error)) {
      reloadOnce(STALE_BUNDLE_RELOAD_KEY);
    }

    // Ошибка вызова — это «не знаем», а не «разлогинен». Прежде здесь возвращался false, и любой
    // сбой (таймаут, обрыв, исчезнувшая server function) перерисовывал интерфейс в гостя: человек
    // ходил по сайту и внезапно оказывался «незалогинен», хотя кука была на месте и F5 всё
    // возвращал. Держим последнее известное состояние — настоящий разлогин приходит от сервера
    // честным ответом isAuth=false со status='ok'.
    return { isAuth: lastKnownIsAuth, status: 'error' };
  }
};
